import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../lib/api";
import { getReport, requestReportPdf } from "../lib/api/index";
import ImplementationModal from "../components/ImplementationModal";
import ReportLoading from "../components/report/ReportLoading";
import ReportError from "../components/report/ReportError";
import ReportBusinessHealth from "../components/report/ReportBusinessHealth";
import ReportSupportingScores from "../components/report/ReportSupportingScores";
import ReportScoreboard from "../components/report/ReportScoreboard";
import ReportLossCard from "../components/report/ReportLossCard";
import ReportActionBar from "../components/report/ReportActionBar";
import ReportHowToRead from "../components/report/ReportHowToRead";
import ReportSections from "../components/report/ReportSections";
import ReportCategories from "../components/report/ReportCategories";
import ReportFinalCTA from "../components/report/ReportFinalCTA";
import BenchmarkWidget from "../components/shared/BenchmarkWidget";
import { logger } from "../lib/logger";

export default function PaidReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [implOpen, setImplOpen] = useState(false);
  const ctaFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getReport(sessionId)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        trackEvent("report_view", `/audit/report/${sessionId}`, {}, sessionId);
        // Once the report loads, the implementation CTA is visible
        if (!ctaFiredRef.current) {
          ctaFiredRef.current = true;
          trackEvent("implementation_cta_view", `/audit/report/${sessionId}`, {}, sessionId);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error("paid_report_load_failed", { sessionId, status: err?.response?.status });
        const code = err?.response?.status;
        if (code === 402) {
          navigate(`/audit/preview/${sessionId}`, { replace: true });
        } else if (code === 425) {
          setError("Your report is still being generated. Refresh in a moment.");
        } else if (code === 404) {
          setError("Report not found.");
        } else {
          setError("Could not load the report. Please refresh.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const downloadPdf = useCallback(async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const data = await requestReportPdf(sessionId);
      const url = data?.download_url;
      if (!url) throw new Error("no_download_url");
      // Open in a new tab — browser will handle the PDF stream
      window.open(url, "_blank", "noopener");
    } catch (err) {
      logger.error("pdf_generate_failed", { sessionId, err: err?.response?.data || err?.message });
      setPdfError("Could not generate the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  }, [sessionId, pdfBusy]);

  if (loading) {
    return <ReportLoading />;
  }

  if (error) {
    return <ReportError error={error} />;
  }

  // Consume the 26-field paid-report view-model contract from
  // `backend/services/report_view_model.py`. Field names + defaults are
  // CEO-locked and must remain byte-identical to that builder.
  const {
    business_name,
    revenue_leak_score,
    opportunity_score,
    automation_readiness_score,
    estimated_monthly_loss_min,
    estimated_monthly_loss_max,
    estimated_monthly_loss_midpoint,
    leak_categories_affected = [],
    full_report,
    // --- P1 Business Health additive payload ---
    business_health,
    revenue_recovery,
    opportunity,
    automation,
    operations,
    retention,
    executive_summary,
  } = report || {};
  const sections = full_report?.sections || [];

  const supportingScores = [
    { id: "revenue_recovery",  metricLabel: "Revenue Recovery",        block: revenue_recovery },
    { id: "opportunity",       metricLabel: "Opportunity",             block: opportunity },
    { id: "automation",        metricLabel: "Automation Readiness",    block: automation },
    { id: "operations",        metricLabel: "Operations",              block: operations },
    { id: "retention",         metricLabel: "Retention",               block: retention },
  ].filter((s) => s.block && typeof s.block.score === "number");

  const openImplModal = () => setImplOpen(true);
  const onViewNextSteps = () => navigate(`/audit/next-steps/${sessionId}`);

  return (
    <div className="sf-container" style={{ padding: "48px 24px", maxWidth: 960 }} data-testid="paid-report-page">
      <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Confidential business audit</div>
      <h1 className="sf-display" style={{ fontSize: 40, lineHeight: 1.1, marginBottom: 10 }} data-testid="paid-report-headline">
        {business_name || "Your business"} — full audit.
      </h1>
      <p className="sf-muted-text" style={{ marginBottom: 32, maxWidth: 760 }}>
        Thirteen sections. Numbers verified by the SystemForge Revenue Leak Engine. AI narration locked to those numbers.
      </p>

      {/* Top scoreboard */}
      <ReportBusinessHealth
        businessHealth={business_health}
        executiveSummary={executive_summary}
      />

      <ReportSupportingScores supportingScores={supportingScores} />

      <BenchmarkWidget score={business_health?.score} testidPrefix="paid-report" />

      <ReportScoreboard
        revenueLeakScore={revenue_leak_score}
        opportunityScore={opportunity_score}
        automationReadinessScore={automation_readiness_score}
      />

      <ReportLossCard
        estimatedMonthlyLossMin={estimated_monthly_loss_min}
        estimatedMonthlyLossMax={estimated_monthly_loss_max}
        estimatedMonthlyLossMidpoint={estimated_monthly_loss_midpoint}
      />

      {/* Action bar */}
      <ReportActionBar
        pdfBusy={pdfBusy}
        pdfError={pdfError}
        onDownloadPdf={downloadPdf}
        onRequestImplementation={openImplModal}
      />

      {/* P3 — "How to read this report" intro ----------------------- */}
      <ReportHowToRead />

      {/* 13 sections */}
      <ReportSections sections={sections} />

      <ReportCategories
        leakCategoriesAffected={leak_categories_affected}
        estimatedMonthlyLossMin={estimated_monthly_loss_min}
        estimatedMonthlyLossMax={estimated_monthly_loss_max}
      />

      {/* Final CTA strip — P3 single ascension callout. NO new tiers / pricing logic. */}
      <ReportFinalCTA
        onViewNextSteps={onViewNextSteps}
        onRequestImplementation={openImplModal}
      />

      <p className="sf-mono" style={{ fontSize: 11, color: "var(--sf-muted)", lineHeight: 1.7, marginTop: 36, letterSpacing: "0.02em", maxWidth: 760 }} data-testid="paid-report-engine-disclosure">
        Your scores come from a deterministic engine that maps your 12 answers to 8 system categories. The AI you read narrates the engine&#39;s findings; it does not calculate them.
      </p>

      <ImplementationModal
        open={implOpen}
        onClose={() => setImplOpen(false)}
        sessionId={sessionId}
        defaultEmail=""
        defaultBusiness={business_name || ""}
      />
    </div>
  );
}
